import { logger } from '../../services/logger';
import React from 'react';
import { Link } from 'react-router-dom';
import { Market } from '../../types';
import { supabase } from '../../lib/db';
import { useSettings } from '../../lib/settings/settingsContext';
import { Loader2, AlertTriangle, TrendingUp, MinusCircle, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { formatDateWithTimezone } from '../../utils/dateUtils';
import { ClickableUserName } from '../common/ClickableUserName';

// --- Helper Functions ---
const safeDivide = (numerator: number, denominator: number): number => {
  if (denominator === 0 || !denominator) {
    return 0;
  }
  return numerator / denominator;
};

// --- Interfaces ---
interface MarketStatsProps {
  market: Market;
  marketId?: string;
}

// Interface for the database function result
interface MarketStatsDbResult {
  // Market level stats
  total_volume: number;
  total_pool: number;
  total_bets: number;
  unique_traders: number;
  min_bet: number;
  max_bet: number;
  average_bet: number;
  service_fees_collected: number;
  active_bets_count: number;
  cancelled_bets_count: number;
  cancelled_bets_volume: number;
  
  // Option level stats
  option_id: number;
  option_name: string;
  pool_volume: number;
  active_bet_count: number;
  cancelled_bet_count: number;
  won_bet_count: number;
  lost_bet_count: number;
  min_bet_amount: number | null;
  max_bet_amount: number | null;
  avg_bet_amount: number | null;
  cancellation_volume: number;
  service_fees_option: number;
}

interface CalculatedOutcomeStats {
  option_id: number;
  option_name: string;
  pool_volume: number;
  probability: number;
  implied_return: number;
  cancellation_count: number;
  cancellation_volume: number;
  min_bet_amount: number | null;
  max_bet_amount: number | null;
  avg_bet_amount: number | null;
  is_winning_option?: boolean;
  active_bet_count: number;
  cancelled_bet_count: number;
  won_bet_count: number;
  lost_bet_count: number;
  service_fees_option: number;
}

interface CalculatedMarketStats {
  total_bets_count: number;
  active_bets_count: number;
  cancelled_bets_count: number;
  won_bets_count: number;
  lost_bets_count: number;

  total_pool_volume: number;
  total_cancellation_volume: number;
  total_fees_collected: number | null;

  unique_traders_count: number;

  min_bet_amount_market: number | null;
  max_bet_amount_market: number | null;
  average_bet: number | null;

  // Resolution Specific Stats
  total_winning_volume?: number;
  total_losing_volume?: number;
  average_winning_bet?: number;
  average_losing_bet?: number;
}

interface DetailedStats {
  marketStats: CalculatedMarketStats | null;
  outcomeStats: CalculatedOutcomeStats[];
}

// --- Component ---
export function MarketStats({ market, marketId }: MarketStatsProps) {
  const { formatCurrency } = useSettings();
  const [stats, setStats] = React.useState<DetailedStats>({ marketStats: null, outcomeStats: [] });
  const [loadingStats, setLoadingStats] = React.useState<boolean>(true);
  const [statsError, setStatsError] = React.useState<string | null>(null);
  const [creatorName, setCreatorName] = React.useState<string | null>(null);
  const [loadingCreator, setLoadingCreator] = React.useState<boolean>(false);

  const formatPercentage = (value: number | null | undefined): string => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  const formatReturn = (value: number | null | undefined): string => {
      if (value === null || value === undefined || isNaN(value) || !isFinite(value) || value <= 0) return '-';
      if (value > 100) return '>100x'; // Cap display for extremely low probabilities
      return `${value.toFixed(2)}x`;
  };

  // Fetch market statistics from database
  React.useEffect(() => {
    if (!marketId) {
      setLoadingStats(false);
      setStatsError('Market ID is required to fetch stats.');
      return;
    }
    
    // Log market data for debugging
    console.log('MarketStats received market data:', {
      id: market?.id,
      status: market?.status,
      optionsCount: Array.isArray(market?.market_options) ? market.market_options.length : 'not an array',
      hasOptions: Boolean(market?.market_options),
      optionsType: market?.market_options ? typeof market.market_options : 'undefined'
    });
    
    // Handle missing market data more gracefully
    if (!market) {
      setLoadingStats(false);
      setStatsError('Market data is missing.');
      return;
    }
    
    // Try to normalize the market_options property if it exists but isn't an array
    if (market.market_options && !Array.isArray(market.market_options)) {
      logger.warn('Market options exists but is not an array. Attempting to normalize.');
      try {
        // If it's a string (like JSON), try to parse it
        if (typeof market.market_options === 'string') {
          try {
            const parsedOptions = JSON.parse(market.market_options);
            if (Array.isArray(parsedOptions)) {
              market = { ...market, market_options: parsedOptions };
              logger.info('Successfully parsed market options string into array');
            }
          } catch (e) {
            logger.error('Failed to parse market_options string:', e);
          }
        }
      } catch (e) {
        logger.error('Error normalizing market_options:', e);
      }
    }
    
    // Only return early if both conditions are true: market_options is defined AND it's an empty array
    // This allows markets with undefined market_options to still proceed
    if (Array.isArray(market.market_options) && market.market_options.length === 0) {
      setLoadingStats(false);
      setStatsError('Market options are empty. This market may not be fully configured.');
      return;
    }

    const fetchMarketStats = async () => {
      setLoadingStats(true);
      setStatsError(null);
      try {
        // Call the database function to get all stats in one call
        const { data: statsData, error: statsError } = await supabase
          .rpc('get_market_stats', { market_id_param: marketId });

        if (statsError) {
          throw new Error(`Failed to fetch market stats: ${statsError.message}`);
        }
        
        if (!statsData || statsData.length === 0) {
          logger.warn('No stats data returned from database, using empty fallback data');
          
          // Create basic empty stats structure as a fallback
          const emptyStats = {
            marketStats: {
              total_bets_count: 0,
              active_bets_count: 0,
              cancelled_bets_count: 0,
              won_bets_count: 0,
              lost_bets_count: 0,
              total_pool_volume: 0,
              total_cancellation_volume: 0,
              total_fees_collected: 0,
              unique_traders_count: 0,
              min_bet_amount_market: 0,
              max_bet_amount_market: 0,
              average_bet: 0
            },
            outcomeStats: market.market_options?.map(option => ({
              option_id: option.id,
              option_name: option.name,
              pool_volume: 0,
              probability: 0,
              implied_return: 0,
              cancellation_count: 0,
              cancellation_volume: 0,
              min_bet_amount: 0,
              max_bet_amount: 0,
              avg_bet_amount: 0,
              active_bet_count: 0,
              cancelled_bet_count: 0,
              won_bet_count: 0,
              lost_bet_count: 0,
              service_fees_option: 0
            })) || []
          };
          
          setStats(emptyStats);
          setLoadingStats(false);
          return;
        }

        // Log data for debugging
        logger.info('Stats data from database:', statsData);
        
        // 3. Process the results from the database
        // The first row contains the market-level stats that are the same for all rows
        const totalPoolVolume = statsData[0].total_pool || 0;
        const totalBets = statsData[0].total_bets || 0;
        const uniqueTraders = statsData[0].unique_traders || 0;
        const minBet = statsData[0].min_bet || 0;
        const maxBet = statsData[0].max_bet || 0;
        const avgBet = statsData[0].average_bet || 0;
        const serviceFeesCollected = statsData[0].service_fees_collected || 0;
        const activeBetsCount = statsData[0].active_bets_count || 0;
        const cancelledBetsCount = statsData[0].cancelled_bets_count || 0;
        const cancelledBetsVolume = statsData[0].cancelled_bets_volume || 0;
        
        // Process per-option stats
        const isResolved = market.winning_option_id !== null && market.winning_option_id !== undefined;
        const winningOptionId = market.winning_option_id;
        
        // Transform database results into our component's expected format
        const outcomeStats = statsData.map((stat: MarketStatsDbResult) => {
          // Calculate probability based on pool volumes
          const probability = safeDivide(stat.pool_volume || 0, totalPoolVolume);
          return {
            option_id: stat.option_id,
            option_name: stat.option_name,
            pool_volume: stat.pool_volume || 0,
            probability: probability,
            implied_return: safeDivide(1, probability),
            cancellation_count: stat.cancelled_bet_count || 0,
            cancellation_volume: stat.cancellation_volume || 0,
            min_bet_amount: stat.min_bet_amount || null,
            max_bet_amount: stat.max_bet_amount || null,
            avg_bet_amount: stat.avg_bet_amount || null,
            is_winning_option: isResolved ? stat.option_id === winningOptionId : undefined,
            active_bet_count: stat.active_bet_count || 0,
            cancelled_bet_count: stat.cancelled_bet_count || 0,
            won_bet_count: stat.won_bet_count || 0,
            lost_bet_count: stat.lost_bet_count || 0,
            service_fees_option: stat.service_fees_option || 0
          };
        });
        
        // Calculate aggregate statistics
        const won_bets_count = statsData.reduce((sum: number, stat: MarketStatsDbResult) => sum + (stat.won_bet_count || 0), 0);
        const lost_bets_count = statsData.reduce((sum: number, stat: MarketStatsDbResult) => sum + (stat.lost_bet_count || 0), 0);
        
        // Calculate winning/losing volumes
        const total_winning_volume = isResolved ? statsData
          .filter((stat: MarketStatsDbResult) => stat.option_id === winningOptionId)
          .reduce((sum: number, stat: MarketStatsDbResult) => sum + (stat.pool_volume || 0), 0) : 0;
          
        const total_losing_volume = isResolved ? statsData
          .filter((stat: MarketStatsDbResult) => stat.option_id !== winningOptionId)
          .reduce((sum: number, stat: MarketStatsDbResult) => sum + (stat.pool_volume || 0), 0) : 0;
        
        // Create the final stats object
        const calculatedMarketStats: CalculatedMarketStats = {
          total_bets_count: totalBets,
          active_bets_count: activeBetsCount,
          cancelled_bets_count: cancelledBetsCount,
          won_bets_count: won_bets_count,
          lost_bets_count: lost_bets_count,
          
          total_pool_volume: totalPoolVolume,
          total_cancellation_volume: cancelledBetsVolume,
          total_fees_collected: serviceFeesCollected,
          
          unique_traders_count: uniqueTraders,
          
          min_bet_amount_market: minBet,
          max_bet_amount_market: maxBet,
          average_bet: avgBet,
          
          total_winning_volume: isResolved ? total_winning_volume : undefined,
          total_losing_volume: isResolved ? total_losing_volume : undefined,
          average_winning_bet: isResolved ? safeDivide(total_winning_volume, won_bets_count) : undefined,
          average_losing_bet: isResolved ? safeDivide(total_losing_volume, lost_bets_count) : undefined,
        };
        
        // Update the component state
        setStats({
          marketStats: calculatedMarketStats,
          outcomeStats: outcomeStats,
        });

      } catch (error: unknown) {
        logger.error('Error fetching or calculating detailed stats:', error);
        const message = error instanceof Error ? error.message : 'An unexpected error occurred';
        setStatsError(`Failed to load statistics: ${message}`);
        setStats({ marketStats: null, outcomeStats: [] });
      } finally {
        setLoadingStats(false);
      }
    };

    fetchMarketStats();
  }, [marketId, market]);

  // Fetch creator details
  React.useEffect(() => {
    if (!market?.created_by) return;

    const fetchCreatorDetails = async () => {
      setLoadingCreator(true);
      try {
        // Fetch user details from the users table
        const { data, error } = await supabase
          .from('users')
          .select('user_metadata')
          .eq('id', market.created_by)
          .maybeSingle(); // Use maybeSingle instead of single to handle missing users

        if (error) {
          logger.warn('Error fetching creator details:', error.message);
          setCreatorName(`Unknown (ID: ${market.created_by.substring(0, 8)}...)`);
          return;
        }

        if (data && data.user_metadata) {
          // Extract first_name and last_name from user_metadata
          const metadata = data.user_metadata;
          const firstName = metadata.first_name || metadata.firstName || '';
          const lastName = metadata.last_name || metadata.lastName || '';
          
          if (firstName || lastName) {
            setCreatorName(`${firstName} ${lastName}`.trim());
          } else {
            setCreatorName(`User (ID: ${market.created_by.substring(0, 8)}...)`);
          }
        } else {
          setCreatorName(`Unknown (ID: ${market.created_by.substring(0, 8)}...)`);
        }
      } catch (err) {
        logger.error('Failed to fetch creator details:', err);
        setCreatorName(`Error (ID: ${market.created_by.substring(0, 8)}...)`);
      } finally {
        setLoadingCreator(false);
      }
    };

    fetchCreatorDetails();
  }, [market?.created_by]);

  // --- Render Logic ---
  const { marketStats, outcomeStats } = stats;
  const isResolved = market.winning_option_id !== null && market.winning_option_id !== undefined;

  // Basic Market Info Section
  const renderMarketInfo = () => (
    // Card styling for this section
    <div className="mb-6 bg-white p-6 rounded-lg shadow border border-gray-200">
      <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Market Details</h3>
      {/* Display Question prominently first if it exists */}
      {market.question && (
          <p className="mb-4 text-base text-gray-800 font-medium"><span className="font-semibold text-gray-600">Question:</span> {market.question}</p>
      )}
      {/* Increased columns for more data */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-sm mt-4">
        {/* Existing fields */}
        <div><span className="font-medium text-gray-600">Title:</span> <span className="text-gray-900">{market.title}</span></div>
        <div><span className="font-medium text-gray-600">Market ID:</span> <span className="text-gray-900 font-mono text-xs">{market.id}</span></div>
        {/* New field: Short Code */}
        {market.short_code && <div><span className="font-medium text-gray-600">Short Code:</span> <span className="text-gray-900 font-mono text-xs">{market.short_code}</span></div>}
        <div><span className="font-medium text-gray-600">Category:</span> <span className="text-gray-900">{market.category || 'N/A'}</span></div>
        {/* New field: Subcategory */}
        {market.subcategory && <div><span className="font-medium text-gray-600">Subcategory:</span> <span className="text-gray-900">{market.subcategory}</span></div>}
        <div><span className="font-medium text-gray-600">Type:</span> <span className="text-gray-900">{market.market_type || 'N/A'}</span></div>
        <div><span className="font-medium text-gray-600">Visibility:</span> <span className="text-gray-900">{market.is_private ? 'Private' : 'Public'}</span></div>
        <div><span className="font-medium text-gray-600">Creator Fee:</span> <span className="text-gray-900">{market.fixed_fee ? `${market.fixed_fee}%` : 'N/A'}</span></div>
        {/* New field: Featured Status */}
        <div>
            <span className="font-medium text-gray-600">Featured:</span> {' '}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${market.is_featured ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                {market.is_featured ? 'Yes' : 'No'}
            </span>
        </div>
        {/* New field: Verified Status */}
        <div>
            <span className="font-medium text-gray-600">Verified:</span> {' '}
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${market.is_verified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {market.is_verified ? 'Yes' : 'No'}
            </span>
        </div>
        <div>
              <span className="font-medium text-gray-600">Status:</span> {' '}
              <span className={`font-semibold ${statusTextColor(market.status)}`}>
                 {market.status ? market.status.charAt(0).toUpperCase() + market.status.slice(1) : 'N/A'}
              </span>
         </div>
        <div><span className="font-medium text-gray-600">Scheduled Resolution:</span> <span className="text-gray-900">{market.resolution_time ? formatDateWithTimezone(market.resolution_time) : 'N/A'}</span></div>
        {/* New field: Actual Resolved Time */}
        {market.resolved_time && <div><span className="font-medium text-gray-600">Resolved At:</span> <span className="text-gray-900">{formatDateWithTimezone(market.resolved_time)}</span></div>}
        {/* New field: Resolution Source */}
        {market.resolution_source && <div><span className="font-medium text-gray-600">Resolution Source:</span> <span className="text-gray-900">{market.resolution_source}</span></div>}

         {isResolved && market.winning_option_id && (
              <div className="md:col-span-1"><span className="font-medium text-gray-600">Winning Outcome:</span> <span className="text-gray-900 font-semibold">
                {Array.isArray(market.market_options) 
                  ? (market.market_options.find(o => o.id === market.winning_option_id)?.name || `Unknown (ID: ${market.winning_option_id})`) 
                  : `Option ID: ${market.winning_option_id}`}
              </span></div>
         )}

        {/* Creator and Timestamps */}
        <div>
          <span className="font-medium text-gray-600">Creator:</span>{' '}
          {loadingCreator ? (
            <span className="text-gray-500">Loading...</span>
          ) : creatorName && market.created_by ? (
            <ClickableUserName userId={market.created_by} displayName={creatorName} />
          ) : (
            <span className="text-gray-900 font-mono text-xs">{market.created_by || 'Unknown'}</span>
          )}
        </div>
        {market.created_at && <div><span className="font-medium text-gray-600">Created At:</span> <span className="text-gray-900">{new Date(market.created_at).toLocaleString()}</span></div>}
        {market.updated_at && <div><span className="font-medium text-gray-600">Last Updated:</span> <span className="text-gray-900">{new Date(market.updated_at).toLocaleString()}</span></div>}
      </div>
      {/* Display Description below the grid */}
      <p className="mt-4 text-sm text-gray-700"><span className="font-medium text-gray-600">Description:</span> {market.description || 'No description provided.'}</p>
      {/* New field: Resolution Note (conditional) */}
      {isResolved && market.resolution_note && (
          <p className="mt-2 text-sm text-gray-700"><span className="font-medium text-gray-600">Resolution Note:</span> {market.resolution_note}</p>
      )}
      {/* New field: Cancellation Reason (conditional) */}
      {market.status === 'cancelled' && market.cancellation_reason && (
          <p className="mt-2 text-sm text-red-700"><span className="font-medium text-red-600">Cancellation Reason:</span> {market.cancellation_reason}</p>
      )}
    </div>
  );

  // Helper function for status text color (can be expanded)
  const statusTextColor = (status: Market['status'] | undefined | null) => {
    switch (status) {
        case 'resolved': return 'text-green-600';
        case 'active': return 'text-blue-600';
        case 'ended': return 'text-orange-600';
        case 'cancelled': return 'text-red-600';
        case 'pending_approval': return 'text-yellow-600';
        case 'rejected': return 'text-red-700';
        default: return 'text-gray-600';
    }
  };

  // Overall Stats Section
  const renderOverallStats = () => {
      if (!marketStats) return null;
      return (
          // Card styling for this section
          <div className="mb-6 bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Overall Market Statistics</h3>
              {/* Responsive grid: 1 col mobile, 2 cols sm, 3 cols lg, 4 cols xl */}
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 xl:grid-cols-5 gap-4 mt-4">
                   <StatCard title="Total Market Size" value={formatCurrency(marketStats.total_pool_volume)} icon={<TrendingUp className="text-green-500 w-5 h-5"/>} tooltip="Sum of all currently active trade amounts."/>
                   <StatCard title="Min Trade" value={formatCurrency(marketStats.min_bet_amount_market ?? 0)} tooltip="Smallest active trade amount across the entire market." />
                   <StatCard title="Max Trade" value={formatCurrency(marketStats.max_bet_amount_market ?? 0)} tooltip="Largest active trade amount across the entire market." />
                   <StatCard title="Average Trade" value={formatCurrency(marketStats.average_bet ?? 0)} tooltip="Average trade amount across the entire market." />
                   <StatCard title="Unique Traders" value={marketStats.unique_traders_count ?? 'N/A'} tooltip="Number of distinct users who placed trades." />
                   <StatCard title="Service Fees Collected" value={formatCurrency(marketStats.total_fees_collected ?? 0)} tooltip="Total fees earned by the platform from this market." />
                   <StatCard title="Total Trades Placed" value={marketStats.total_bets_count ?? 'N/A'} tooltip="Total number of trade transactions (including active, cancelled, resolved)." />
                   <StatCard title="Trades" value={marketStats.active_bets_count ?? 'N/A'} tooltip="Number of trades currently active (or contributing to the resolved market size if market is resolved)." />
                   <StatCard title="Cancelled Trades (Count)" value={marketStats.cancelled_bets_count ?? 'N/A'} icon={<MinusCircle className="text-orange-500 w-5 h-5"/>} tooltip="Number of trades cancelled by users." />
                   <StatCard title="Cancelled Trades (Volume)" value={formatCurrency(marketStats.total_cancellation_volume)} icon={<MinusCircle className="text-orange-500 w-5 h-5"/>} tooltip="Total value of trades cancelled by users." />
              </div>
          </div>
      );
  };

 // Per Outcome Table Section
  const renderOutcomeTable = () => (
      // Card styling for this section, padding removed for table edge-to-edge feel
      <div className="mb-6 bg-white p-0 rounded-lg shadow border border-gray-200 overflow-hidden">
           {/* Header inside the card, with padding */}
           <h3 className="text-lg font-semibold text-gray-800 px-6 pt-4 pb-2 border-b">Per Outcome Analysis</h3>
           {/* Scrollable container for the table */}
           <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                      <tr>
                          {/* Using ThWithTooltip for headers */}
                          <ThWithTooltip className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="The possible outcomes for this market.">Outcome</ThWithTooltip>
                          {isResolved && <ThWithTooltip className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Indicates if the outcome was winning (green check) or losing (red cross).">Status</ThWithTooltip>}
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Total amount of active trades placed on this outcome.">Market Size</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="The current market probability derived from this outcome's market size relative to the total market size.">Probability</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="The theoretical return multiplier if this outcome resolves as the winner (1 / Probability). Capped at >100x for display.">Implied Return</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Number of active trades on this outcome.">Trades</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Number of cancelled trades on this outcome.">Cancelled Trades</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Number of won trades on this outcome.">Won Trades</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Number of lost trades on this outcome.">Lost Trades</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Total value of trades cancelled on this outcome.">Cancelled Vol</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Smallest active trade amount placed on this outcome.">Min Trade</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Largest active trade amount placed on this outcome.">Max Trade</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Average trade amount placed on this outcome.">Avg Trade</ThWithTooltip>
                          <ThWithTooltip className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" tooltip="Service fees collected from trades on this outcome.">Fees</ThWithTooltip>
                      </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                      {outcomeStats.length > 0 ? outcomeStats.map((o) => (
                          // Added hover effect and transition
                          <tr key={o.option_id} className={`${o.is_winning_option ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-gray-50'} transition-colors duration-150`}>
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 truncate max-w-xs">{o.option_name}</td>
                              {isResolved && (
                                  <td className="px-2 py-3 whitespace-nowrap text-center text-sm">
                                      {o.is_winning_option ? <CheckCircle className="w-5 h-5 text-green-500 inline"/> : <XCircle className="w-5 h-5 text-red-500 inline"/>}
                                  </td>
                              )}
                              {/* Ensured right alignment for numeric data */}
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{formatCurrency(o.pool_volume)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{formatPercentage(o.probability)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{formatReturn(o.implied_return)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.active_bet_count || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.cancelled_bet_count || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.won_bet_count || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.lost_bet_count || 0}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{formatCurrency(o.cancellation_volume)}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.min_bet_amount !== null ? formatCurrency(o.min_bet_amount) : '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.max_bet_amount !== null ? formatCurrency(o.max_bet_amount) : '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{o.avg_bet_amount !== null ? formatCurrency(o.avg_bet_amount) : '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 text-right font-mono">{formatCurrency(o.service_fees_option || 0)}</td>
                          </tr>
                      )) : (
                          <tr>
                              <td colSpan={isResolved ? 14 : 13} className="px-6 py-4 text-center text-sm text-gray-500">No outcome data available.</td>
                          </tr>
                      )}
                  </tbody>
              </table>
          </div>
      </div>
  );

  // Resolution Stats Section (Conditional)
 const renderResolutionStats = () => {
     if (!isResolved || !marketStats) return null;
     return (
         // Card styling for this section
         <div className="mt-6 bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 border-b pb-2">Resolution Analysis</h3>
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-4">
                    <StatCard title="Total Winning Volume" value={formatCurrency(marketStats.total_winning_volume ?? 0)} icon={<CheckCircle className="text-green-500 w-5 h-5"/>} tooltip="Total amount traded on the winning outcome(s)." />
                    <StatCard title="Winning Trades Count" value={marketStats.won_bets_count ?? 'N/A'} icon={<CheckCircle className="text-green-500 w-5 h-5"/>} tooltip="Number of trades on the winning outcome(s)." />
                    <StatCard title="Average Winning Trade" value={formatCurrency(marketStats.average_winning_bet ?? 0)} tooltip="Average amount of a winning trade (Total Winning Volume / Winning Trades Count)." />
                    <StatCard title="Total Losing Volume" value={formatCurrency(marketStats.total_losing_volume ?? 0)} icon={<XCircle className="text-red-500 w-5 h-5"/>} tooltip="Total amount traded on losing outcomes." />
                    <StatCard title="Losing Trades Count" value={marketStats.lost_bets_count ?? 'N/A'} icon={<XCircle className="text-red-500 w-5 h-5"/>} tooltip="Number of trades on losing outcomes." />
                    <StatCard title="Average Losing Trade" value={formatCurrency(marketStats.average_losing_bet ?? 0)} tooltip="Average amount of a losing trade (Total Losing Volume / Losing Trades Count)." />
               </div>
         </div>
     );
 };

 // --- Main Component Return ---
  return (
    // Adjusted main container padding and background
    <div className="p-4 md:p-6 bg-gray-100 rounded-lg shadow-inner min-h-screen">
      {/* Main Title */}
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Market Analysis: <span className="font-normal">{market.title}</span></h2>

      {/* Loading State */}
      {loadingStats && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <p className="ml-4 text-lg text-gray-700">Calculating Market Statistics...</p>
        </div>
      )}

      {/* Error State */}
      {statsError && !loadingStats && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md shadow" role="alert">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-3 flex-shrink-0" />
            <div>
                <p className="font-bold">Error Loading Statistics</p>
                <p className="text-sm">{statsError}</p>
                
                {/* Add debugging information in development mode */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs font-mono">
                    <p>Debug info:</p>
                    <ul className="list-disc pl-4">
                      <li>Market ID: {market?.id || 'undefined'}</li>
                      <li>Status: {market?.status || 'undefined'}</li>
                      <li>Has market_options: {market?.market_options ? 'Yes' : 'No'}</li>
                      <li>Options count: {market?.market_options?.length || 0}</li>
                    </ul>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Content Display */}
      {!loadingStats && !statsError && marketStats && (
        // Using fragments to group sections without extra divs
        <>
          {renderMarketInfo()}
          {renderOverallStats()}
          {renderOutcomeTable()}
          {renderResolutionStats()}
        </>
      )}

       {/* No Stats Calculated State */}
       {!loadingStats && !statsError && !marketStats && (
           <div className="text-center py-8 text-gray-500 bg-white p-6 rounded-lg shadow border border-gray-200">
               <HelpCircle className="w-10 h-10 mx-auto mb-2 text-gray-400"/>
               No statistics could be calculated. This might happen if there are no trades yet or if market data is incomplete.
           </div>
       )}
    </div>
  );
}

// Helper component for stat cards with optional icon and tooltip
const StatCard = ({ title, value, icon, tooltip }: { title: string; value: string | number; icon?: React.ReactNode, tooltip?: string }) => (
  // Adding min-height to prevent layout shifts during loading/value changes
  <div className="bg-white p-4 rounded-lg shadow border border-gray-200 group relative min-h-[90px] flex flex-col justify-between">
    <div> {/* Wrapper for top content */}
        <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-gray-500 truncate pr-2">{title}</p>
            {icon && <span className="text-gray-400 flex-shrink-0">{icon}</span>}
        </div>
        <p className="text-2xl font-semibold text-gray-900 truncate" title={typeof value === 'string' ? value : String(value)}>{value}</p>
    </div>
     {tooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
              {tooltip}
              <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-[-4px] w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-800"></div> {/* Arrow */}
          </div>
      )}
  </div>
);

// Helper component for Table Header Tooltips
const ThWithTooltip = ({ children, tooltip, className }: { children: React.ReactNode; tooltip: string; className?: string }) => (
    <th scope="col" className={`group relative ${className}`}>
        {children}
        {tooltip && (
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max max-w-xs px-3 py-1.5 bg-gray-800 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10 shadow-lg">
                {tooltip}
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-[-4px] w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-800"></div> {/* Arrow */}
            </div>
        )}
    </th>
); 